import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { Star } from 'lucide-react';
import { normalizeRating } from '../utils/ratingHelper';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BrowsePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { viewMode, setViewMode, user } = useAuth();
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [cursor, setCursor] = useState(null);
  const [savedTradies, setSavedTradies] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [showNewListings, setShowNewListings] = useState(false);
  const observerTarget = useRef(null);
  const debounceTimer = useRef(null);
  const abortController = useRef(null);
  
  const [filters, setFilters] = useState({
    category_id: 'all',
    suburb: '',
    state: 'all',
    min_rating: 'none',
    sort: 'relevance',
  });
  
  useEffect(() => {
    fetchCategories();
    if (user && user.role === 'customer') {
      fetchSavedTradies();
    }
    
    // Parse URL params
    const params = new URLSearchParams(location.search);
    const newFilters = { ...filters };
    if (params.get('q')) {
      setSearchQuery(params.get('q'));
      setDebouncedQuery(params.get('q'));
    }
    if (params.get('suburb')) newFilters.suburb = params.get('suburb');
    if (params.get('sort')) newFilters.sort = params.get('sort');
    if (params.get('view')) setViewMode(params.get('view'));
    if (params.get('category')) {
      const slug = params.get('category');
      fetchCategories().then(() => {
        const cat = categories.find(c => c.slug === slug);
        if (cat) {
          setFilters(prev => ({ ...prev, category_id: cat.id }));
        }
      });
    }
    if (newFilters.suburb !== filters.suburb) {
      setFilters(newFilters);
    }
  }, [location.search]);

  // Debounce search query
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Instant commit on clear
    if (searchQuery === '') {
      setDebouncedQuery('');
      return;
    }

    // Minimum 2 chars for auto-search
    if (searchQuery.length < 2) {
      return;
    }

    // Debounce 400ms
    debounceTimer.current = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 400);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [searchQuery]);

  // Fetch on debounced query or filters change
  useEffect(() => {
    // Reset and fetch from beginning
    setServices([]);
    setCursor(null);
    setHasMore(true);
    fetchServices(true);
    
    // Update URL
    updateURL();
  }, [debouncedQuery, filters]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchSavedTradies = async () => {
    try {
      const response = await axios.get(`${API}/favorites/tradies`);
      const ids = new Set(response.data.tradies.map(t => t.id));
      setSavedTradies(ids);
    } catch (error) {
      console.error('Failed to fetch saved tradies:', error);
    }
  };

  const toggleSave = async (tradieId, e) => {
    e.stopPropagation();
    if (!user) {
      toast.error('Please login to save tradies');
      navigate('/login');
      return;
    }

    if (user.role !== 'customer') {
      toast.error('Only customers can save tradies');
      return;
    }

    try {
      if (savedTradies.has(tradieId)) {
        await axios.delete(`${API}/favorites/tradies/${tradieId}`);
        setSavedTradies(prev => {
          const newSet = new Set(prev);
          newSet.delete(tradieId);
          return newSet;
        });
        toast.success('Removed from favorites');
      } else {
        await axios.post(`${API}/favorites/tradies/${tradieId}`);
        setSavedTradies(prev => new Set([...prev, tradieId]));
        toast.success('Added to favorites');
      }
    } catch (error) {
      console.error('Failed to toggle save:', error);
      toast.error('Failed to update favorites');
    }
  };

  const updateURL = () => {
    const params = new URLSearchParams();
    if (debouncedQuery) params.set('q', debouncedQuery);
    if (filters.category_id !== 'all') params.set('category', filters.category_id);
    if (filters.suburb) params.set('suburb', filters.suburb);
    if (filters.state !== 'all') params.set('state', filters.state);
    if (filters.min_rating !== 'none') params.set('rating', filters.min_rating);
    if (filters.sort !== 'relevance') params.set('sort', filters.sort);
    if (viewMode) params.set('view', viewMode);
    
    const newURL = `${location.pathname}?${params.toString()}`;
    window.history.replaceState({}, '', newURL);
    
    // Save to localStorage
    localStorage.setItem('browse_state', JSON.stringify({
      query: debouncedQuery,
      filters,
      viewMode
    }));
  };

  const fetchServices = async (reset = false) => {
    if (reset) {
      setLoading(true);
      // Cancel any in-flight request
      if (abortController.current) {
        abortController.current.abort();
      }
      abortController.current = new AbortController();
    } else {
      setLoadingMore(true);
    }
    
    try {
      const params = new URLSearchParams();
      if (filters.category_id && filters.category_id !== 'all') params.append('category_id', filters.category_id);
      if (filters.suburb && filters.suburb.trim()) params.append('suburb', filters.suburb);
      if (filters.state && filters.state !== 'all') params.append('state', filters.state);
      if (filters.min_rating && filters.min_rating !== 'none') params.append('min_rating', filters.min_rating);
      if (debouncedQuery && debouncedQuery.trim()) params.append('q', debouncedQuery);
      if (filters.sort) params.append('sort', filters.sort);
      if (!reset && cursor) params.append('cursor', cursor);
      params.append('limit', '20');
      
      const response = await axios.get(`${API}/services?${params}`, {
        signal: abortController.current?.signal
      });
      
      const newServices = response.data.services || [];
      const newCursor = response.data.cursor;
      const total = response.data.total || 0;
      
      if (reset) {
        setServices(newServices);
        announceResults(total);
      } else {
        setServices(prev => [...prev, ...newServices]);
      }
      
      setTotalCount(total);
      setCursor(newCursor);
      setHasMore(newServices.length === 20 && newCursor !== null);
    } catch (error) {
      if (error.name === 'CanceledError') {
        console.log('Request canceled');
        return;
      }
      console.error('Failed to fetch services:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const announceResults = (count) => {
    // ARIA live region announcement
    const announcement = `Results updated: ${count} item${count !== 1 ? 's' : ''}`;
    const liveRegion = document.getElementById('search-live-region');
    if (liveRegion) {
      liveRegion.textContent = announcement;
    }
  };

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && !loadingMore) {
          fetchServices(false);
        }
      },
      { threshold: 0.5 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [hasMore, loading, loadingMore, cursor]);

  // Handle instant search commit (Enter or Search button)
  const handleSearchCommit = () => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    setDebouncedQuery(searchQuery);
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchQuery('');
    setDebouncedQuery('');
  };

  const getGSTBadge = (priceMode) => {
    switch (priceMode) {
      case 'gst_included':
        return <Badge variant="outline" className="text-xs">GST included</Badge>;
      case 'plus_gst':
        return <Badge variant="outline" className="text-xs">+GST</Badge>;
      case 'no_gst':
        return <Badge variant="outline" className="text-xs">No GST</Badge>;
      default:
        return null;
    }
  };

  const ServiceCard = ({ service }) => (
    <Card
      className="group cursor-pointer hover:shadow-xl transition-all duration-300 hover:-translate-y-1 relative overflow-hidden border border-border hover:border-blue-200 dark:hover:border-blue-700 bg-card"
      onClick={() => navigate(`/tradies/services/${service.id}`)}
      data-testid={`service-card-${service.id}`}
    >
      {/* Hover Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-purple-50/50 dark:from-blue-950/20 dark:to-purple-950/20 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"></div>
      
      {/* Heart icon for favorites */}
      {user && user.role === 'customer' && (
        <button
          onClick={(e) => toggleSave(service.tradie_id, e)}
          className="absolute top-4 right-4 z-10 p-2.5 bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm rounded-full hover:scale-110 transition-transform shadow-lg border border-gray-200 dark:border-gray-700"
          data-testid={`save-${service.id}`}
        >
          <svg className={`w-5 h-5 ${savedTradies.has(service.tradie_id) ? 'fill-red-500 text-red-500' : 'fill-none text-gray-400'}`} viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
          </svg>
        </button>
      )}
      
      {/* Service image or placeholder */}
      {service.photos && service.photos.length > 0 ? (
        <div className="aspect-[4/3] rounded-t-xl overflow-hidden">
          <img 
            src={service.photos[0]} 
            alt={service.title}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        </div>
      ) : service.thumbnail_url ? (
        <div className="aspect-[4/3] rounded-t-xl overflow-hidden">
          <img 
            src={service.thumbnail_url} 
            alt={service.title}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        </div>
      ) : service.tradie?.photos && service.tradie.photos.length > 0 ? (
        <div className="aspect-[4/3] rounded-t-xl overflow-hidden">
          <img 
            src={service.tradie.photos[0]} 
            alt={service.title}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        </div>
      ) : service.media_urls && service.media_urls.length > 0 ? (
        <div className="aspect-[4/3] rounded-t-xl overflow-hidden">
          <img 
            src={service.media_urls[0]} 
            alt={service.title}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        </div>
      ) : (
        <div className="aspect-[4/3] bg-gradient-to-br from-blue-500 to-blue-600 dark:from-blue-700 dark:to-blue-800 rounded-t-xl flex items-center justify-center relative overflow-hidden">
          <div className="absolute inset-0 opacity-20" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='1' fill-rule='evenodd'%3E%3Cpath d='M0 40L40 0H20L0 20M40 40V20L20 40'/%3E%3C/g%3E%3C/svg%3E")`,
          }}></div>
          <svg className="w-20 h-20 text-white/80" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>
      )}
      
      <CardContent className="p-5 space-y-3 relative z-10">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-bold text-lg line-clamp-2 flex-1 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors text-foreground" data-testid="service-title">{service.title}</h3>
        </div>
        
        <div className="flex items-center gap-2" data-testid="service-rating">
          <div className="flex items-center gap-1 bg-yellow-50 dark:bg-yellow-900/30 px-2 py-1 rounded-lg border border-yellow-200 dark:border-yellow-700">
            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
            <span className="text-sm font-semibold text-gray-900 dark:text-yellow-100">{normalizeRating(service.rating_avg).toFixed(1)}</span>
          </div>
          <span className="text-sm text-muted-foreground">({service.rating_count} reviews)</span>
        </div>
        
        <p className="text-sm text-muted-foreground line-clamp-2">{service.short_description}</p>
        
        <div className="flex items-center justify-between pt-2 border-t border-border">
          <div className="space-x-2">
            <span className="text-lg font-bold text-blue-600 dark:text-blue-400">${service.price_base}</span>
            {getGSTBadge(service.price_mode)}
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span>{service.suburb}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const ServiceListItem = ({ service }) => (
    <Card
      className="cursor-pointer hover:shadow-lg transition-all"
      onClick={() => navigate(`/tradies/services/${service.id}`)}
      data-testid={`service-list-item-${service.id}`}
    >
      <CardContent className="p-4">
        <div className="flex gap-4">
          {/* Thumbnail */}
          {service.photos && service.photos.length > 0 ? (
            <div className="w-32 h-24 rounded-xl flex-shrink-0 overflow-hidden">
              <img 
                src={service.photos[0]} 
                alt={service.title}
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
          ) : service.thumbnail_url ? (
            <div className="w-32 h-24 rounded-xl flex-shrink-0 overflow-hidden">
              <img 
                src={service.thumbnail_url} 
                alt={service.title}
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
          ) : service.tradie?.photos && service.tradie.photos.length > 0 ? (
            <div className="w-32 h-24 rounded-xl flex-shrink-0 overflow-hidden">
              <img 
                src={service.tradie.photos[0]} 
                alt={service.title}
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
          ) : service.media_urls && service.media_urls.length > 0 ? (
            <div className="w-32 h-24 rounded-xl flex-shrink-0 overflow-hidden">
              <img 
                src={service.media_urls[0]} 
                alt={service.title}
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
          ) : (
            <div className="w-32 h-24 bg-gradient-to-br from-primary/20 to-accent-blue/20 rounded-xl flex-shrink-0" />
          )}
          
          <div className="flex-1 space-y-2">
            <h3 className="font-semibold text-lg" data-testid="service-title">{service.title}</h3>
            
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-1">
                <span className="text-yellow-500">★</span>
                <span className="font-medium">{normalizeRating(service.rating_avg).toFixed(1)}</span>
              </div>
              
              <div className="space-x-2">
                <span className="font-bold" data-testid="service-price">${service.price_base.toFixed(2)}</span>
                {getGSTBadge(service.price_mode)}
              </div>
              
              <span className="text-muted-foreground" data-testid="service-suburb">{service.suburb}, {service.state}</span>
            </div>
            
            <p className="text-sm text-muted-foreground line-clamp-2">{service.short_description}</p>
          </div>
          
          <Button
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              // Handle message
            }}
            data-testid="message-button"
          >
            Message
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 py-8" data-testid="browse-page">
        {/* Header */}
        <div className="mb-8 space-y-2">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-foreground">Browse Tradies</h1>
              <p className="text-muted-foreground mt-2 text-lg">
                {loading ? 'Loading...' : `${totalCount} active listing${totalCount !== 1 ? 's' : ''} available`}
                {debouncedQuery && ` for "${debouncedQuery}"`}
              </p>
            </div>
          </div>
        </div>

      {/* Find Search Box + View Toggle */}
      <div className="mb-6 flex flex-col md:flex-row gap-4">
        {/* Find Box */}
        <div className="flex-1 flex gap-2">
          <div className="relative flex-1">
            <Input
              placeholder="Search services, categories, suburbs (e.g., plumber leaky roof Morley)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearchCommit()}
              className="rounded-xl pr-10"
              data-testid="find-search-input"
            />
            {searchQuery && (
              <button
                onClick={handleClearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                data-testid="clear-search-button"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          <Button
            onClick={handleSearchCommit}
            className="rounded-xl bg-primary hover:bg-primary-hover"
            data-testid="search-button"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </Button>
        </div>

        {/* View Toggle */}
        <div className="flex gap-2 border border-border rounded-xl p-1" data-testid="view-toggle">
          <button
            onClick={() => setViewMode('grid')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              viewMode === 'grid' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
            }`}
            data-testid="view-grid-button"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
            </svg>
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              viewMode === 'list' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
            }`}
            data-testid="view-list-button"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </div>

      {/* ARIA Live Region for Announcements */}
      <div id="search-live-region" className="sr-only" role="status" aria-live="polite" aria-atomic="true"></div>

      {/* New Listings Available Chip */}
      {showNewListings && (
        <div className="mb-4 flex justify-center">
          <Button
            onClick={() => {
              setShowNewListings(false);
              setServices([]);
              setCursor(null);
              fetchServices(true);
            }}
            variant="outline"
            className="rounded-full border-primary text-primary hover:bg-primary/10"
            data-testid="refresh-listings-button"
          >
            🔄 New listings available — Refresh
          </Button>
        </div>
      )}

      {/* Filters */}
      <div className="mb-8 space-y-4">
        <div className="flex flex-wrap gap-3">
          <Select value={filters.category_id} onValueChange={(value) => setFilters({ ...filters, category_id: value })}>
            <SelectTrigger className="w-[200px] rounded-xl" data-testid="filter-category">
              <SelectValue placeholder="All categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Input
            type="text"
            placeholder="Suburb..."
            value={filters.suburb}
            onChange={(e) => setFilters({ ...filters, suburb: e.target.value })}
            className="w-[180px] px-4 py-2 rounded-xl"
            data-testid="filter-suburb"
          />
          
          <Select value={filters.state} onValueChange={(value) => setFilters({ ...filters, state: value })}>
            <SelectTrigger className="w-[120px] rounded-xl" data-testid="filter-state">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All states</SelectItem>
              <SelectItem value="WA">WA</SelectItem>
              <SelectItem value="NSW">NSW</SelectItem>
              <SelectItem value="VIC">VIC</SelectItem>
              <SelectItem value="QLD">QLD</SelectItem>
              <SelectItem value="SA">SA</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={filters.min_rating} onValueChange={(value) => setFilters({ ...filters, min_rating: value })}>
            <SelectTrigger className="w-[140px] rounded-xl" data-testid="filter-rating">
              <SelectValue placeholder="Min rating" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Any rating</SelectItem>
              <SelectItem value="3">3+ stars</SelectItem>
              <SelectItem value="4">4+ stars</SelectItem>
              <SelectItem value="4.5">4.5+ stars</SelectItem>
              <SelectItem value="5">5 stars</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={filters.sort} onValueChange={(value) => setFilters({ ...filters, sort: value })}>
            <SelectTrigger className="w-[140px] rounded-xl" data-testid="filter-sort">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="relevance">Relevance</SelectItem>
              <SelectItem value="rating">Rating</SelectItem>
              <SelectItem value="newest">Newest</SelectItem>
              <SelectItem value="price">Price</SelectItem>
            </SelectContent>
          </Select>
          
          {(filters.category_id !== 'all' || filters.suburb || filters.state !== 'all' || filters.min_rating !== 'none' || debouncedQuery || filters.sort !== 'relevance') && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setFilters({ category_id: 'all', suburb: '', state: 'all', min_rating: 'none', sort: 'relevance' });
                handleClearSearch();
              }}
              className="rounded-xl text-muted-foreground hover:text-foreground"
              data-testid="clear-filters-button"
            >
              <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Clear all
            </Button>
          )}
        </div>

      {/* Context Chips for Active Filters */}
      {(debouncedQuery || filters.category_id !== 'all' || filters.suburb || filters.state !== 'all' || filters.min_rating !== 'none' || filters.sort !== 'relevance') && (
        <div className="mb-4 flex flex-wrap gap-2" data-testid="context-chips">
          {debouncedQuery && (
            <Badge variant="outline" className="px-3 py-1">
              q: "{debouncedQuery}"
            </Badge>
          )}
          {filters.category_id !== 'all' && (
            <Badge variant="outline" className="px-3 py-1">
              category: {categories.find(c => c.id === filters.category_id)?.name || filters.category_id}
            </Badge>
          )}
          {filters.suburb && (
            <Badge variant="outline" className="px-3 py-1">
              suburb: {filters.suburb}
            </Badge>
          )}
          {filters.state !== 'all' && (
            <Badge variant="outline" className="px-3 py-1">
              state: {filters.state}
            </Badge>
          )}
          {filters.min_rating !== 'none' && (
            <Badge variant="outline" className="px-3 py-1">
              rating: ≥{filters.min_rating}
            </Badge>
          )}
          {filters.sort !== 'relevance' && (
            <Badge variant="outline" className="px-3 py-1">
              sort: {filters.sort}
            </Badge>
          )}
        </div>
      )}

      {/* Services Grid/List */}
      {loading ? (
        <div className={viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6' : 'space-y-4'} data-testid="loading-skeleton">
          {[...Array(8)].map((_, i) => (
            <Card key={i}>
              <Skeleton className="aspect-[4/3] rounded-t-xl" />
              <CardContent className="p-4 space-y-2">
                <Skeleton className="h-6 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-4 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : services.length === 0 ? (
        <div className="text-center py-16" data-testid="empty-state">
          <div className="text-6xl mb-4">🔍</div>
          <h3 className="text-2xl font-semibold mb-2">No services found</h3>
          <p className="text-muted-foreground">Try adjusting your filters</p>
        </div>
      ) : viewMode === 'grid' ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 animate-fade-in" data-testid="services-grid">
            {services.map((service) => (
              <ServiceCard key={service.id} service={service} />
            ))}
          </div>
          
          {/* Infinite Scroll Loading */}
          {loadingMore && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mt-6">
              {[...Array(4)].map((_, i) => (
                <Card key={`skeleton-${i}`}>
                  <Skeleton className="aspect-[4/3] rounded-t-xl" />
                  <CardContent className="p-4 space-y-2">
                    <Skeleton className="h-6 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          
          {/* Intersection Observer Target */}
          {hasMore && !loading && (
            <div ref={observerTarget} className="h-20 flex items-center justify-center">
              <div className="text-sm text-muted-foreground">Loading more...</div>
            </div>
          )}
          
          {!hasMore && services.length > 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <p className="text-sm">You've reached the end of results</p>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="max-w-4xl mx-auto space-y-4 animate-fade-in" data-testid="services-list">
            {services.map((service) => (
              <ServiceListItem key={service.id} service={service} />
            ))}
          </div>
          
          {/* Infinite Scroll Loading - List View */}
          {loadingMore && (
            <div className="space-y-4 mt-4">
              {[...Array(3)].map((_, i) => (
                <Card key={`skeleton-${i}`}>
                  <CardContent className="p-4">
                    <div className="flex gap-4">
                      <Skeleton className="w-32 h-24 rounded-xl" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-6 w-3/4" />
                        <Skeleton className="h-4 w-1/2" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          
          {/* Observer Target */}
          {hasMore && !loading && (
            <div ref={observerTarget} className="h-20 flex items-center justify-center">
              <div className="text-sm text-muted-foreground">Loading more...</div>
            </div>
          )}
          
          {!hasMore && services.length > 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <p className="text-sm">You've reached the end of results</p>
            </div>
          )}
        </>
      )}
        </div>
      </div>
    </div>
  );
};

export default BrowsePage;
